import { useState } from "react";
import { buildEditionViewModel, type EditionViewModel } from "./lib/adapter";
import { fixtureMap, publishedFixture, type FixtureName } from "./fixtures";
import {
  Hero,
  EditionCard,
  Rundown,
  EditorialIntake,
  Transcript,
  AgentTimeline,
  ClaimLedger,
  BlockedExample,
  TelegramCTA,
  PreviousEditions,
  Disclosure,
  LoadingState,
  NoEditionState,
  DemoBanner,
} from "./components";
import { CategoryBrowser } from "./components/CategoryBrowser";

interface AppProps {
  initialFixtureName?: FixtureName;
}

function Sidebar({
  activeSection,
  scrollTo,
}: {
  activeSection: string;
  scrollTo: (id: string) => void;
}) {
  const nav = [
    { id: "home", label: "Home", icon: "⌂" },
    { id: "categories", label: "Browse", icon: "⊞" },
    { id: "latest", label: "Latest Edition", icon: "▶" },
    { id: "rundown", label: "Rundown", icon: "☰" },
    { id: "transcript", label: "Transcript", icon: "¶" },
    { id: "timeline", label: "Timeline", icon: "◷" },
    { id: "ledger", label: "Sources", icon: "✓" },
  ];

  return (
    <aside className="sidebar" role="navigation" aria-label="Main navigation">
      <div className="sidebar-brand">
        <div className="sidebar-dot">
          <div className="sidebar-dot-inner" />
        </div>
        <div className="sidebar-wordmark">
          <span>Overnight</span>
          <span className="sub">Newsroom</span>
        </div>
      </div>

      <nav className="sidebar-nav">
        {nav.map((item) => (
          <button
            key={item.id}
            className={activeSection === item.id ? "active" : ""}
            onClick={() => scrollTo(item.id)}
          >
            <span style={{ fontSize: 20, lineHeight: 1 }}>{item.icon}</span>
            {item.label}
          </button>
        ))}
      </nav>

      <div className="sidebar-divider" />

      <div className="sidebar-section-label">Your Library</div>
      <nav className="sidebar-nav">
        <a
          href="https://t.me/anikul94new"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span style={{ fontSize: 20, lineHeight: 1 }}>✉</span>
          Telegram
        </a>
        <a href="http://localhost:8080/eval-dashboard.html" target="_blank" rel="noopener noreferrer">
          <span style={{ fontSize: 20, lineHeight: 1 }}>📊</span>
          Eval Dashboard
        </a>
      </nav>
    </aside>
  );
}

function NowPlaying({ vm }: { vm: EditionViewModel | null }) {
  if (!vm || vm.page_state === "no_edition") {
    return (
      <div className="now-playing">
        <div className="now-playing-info">
          <div className="now-playing-art">📻</div>
          <div className="now-playing-text">
            <div className="now-playing-title">No edition playing</div>
            <div className="now-playing-sub">Select a bulletin below</div>
          </div>
        </div>
        <div className="now-playing-controls">
          <div className="now-playing-buttons">
            <button className="now-playing-btn" aria-label="Shuffle" disabled>⤧</button>
            <button className="now-playing-btn" aria-label="Previous" disabled>⏮</button>
            <button className="now-playing-play" aria-label="Play" disabled>▶</button>
            <button className="now-playing-btn" aria-label="Next" disabled>⏭</button>
            <button className="now-playing-btn" aria-label="Repeat" disabled>↻</button>
          </div>
        </div>
        <div className="now-playing-right" />
      </div>
    );
  }

  return (
    <div className="now-playing">
      <div className="now-playing-info">
        <div className="now-playing-art">📰</div>
        <div className="now-playing-text">
          <div className="now-playing-title" lang="hi">{vm.title}</div>
          <div className="now-playing-sub">
            {vm.story_count} stories · {vm.claim_counts.pass}/{vm.claim_counts.pass + vm.claim_counts.rewrite + vm.claim_counts.block} claims passed
          </div>
        </div>
      </div>
      <div className="now-playing-controls">
        <div className="now-playing-buttons">
          <button className="now-playing-btn" aria-label="Previous">⏮</button>
          <button className="now-playing-play" aria-label="Play" title="Source-gated Hindi bulletin">▶</button>
          <button className="now-playing-btn" aria-label="Next">⏭</button>
        </div>
        <div className="now-playing-progress">
          <span className="now-playing-time">0:00</span>
          <div className="now-playing-bar">
            <div className="now-playing-bar-fill" style={{ width: "0%" }} />
          </div>
          <span className="now-playing-time">3:03</span>
        </div>
      </div>
      <div className="now-playing-right">
        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
          {vm.page_state.replace(/_/g, " ").toUpperCase()}
        </span>
      </div>
    </div>
  );
}

function App({ initialFixtureName = "published" }: AppProps) {
  const [fixtureName, setFixtureName] = useState<FixtureName>(initialFixtureName);
  const [activeSection, setActiveSection] = useState("home");
  const fixture = fixtureName === "loading" ? null : fixtureMap[fixtureName];
  const vm: EditionViewModel | null = fixture ? buildEditionViewModel(fixture) : null;
  const nextScheduled = vm?.next_scheduled ?? publishedFixture.next_scheduled;
  const stateOptions: FixtureName[] = [
    "published", "blocked", "partial", "no_edition", "corrected", "loading",
  ];

  const scrollTo = (id: string) => {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="app-layout">
      <a className="skip-link" href="#main">Skip to content</a>

      <Sidebar activeSection={activeSection} scrollTo={scrollTo} />

      <div className="main-content">
        <div className="topbar">
          <div className="topbar-arrows">
            <button className="topbar-arrow" aria-label="Go back" onClick={() => window.history.back()}>‹</button>
            <button className="topbar-arrow" aria-label="Go forward" onClick={() => window.history.forward()}>›</button>
          </div>
          <div className="topbar-actions">
            <a href="https://t.me/anikul94new" className="btn-pill btn-pill-outline" target="_blank" rel="noopener noreferrer">
              Join Telegram
            </a>
          </div>
        </div>

        <div className="main-scroll" id="main">
          <div className="content-area">
            <DemoBanner />
            <Hero nextScheduled={nextScheduled} />

            {/* Categories Section */}
            <div id="categories">
              <div className="section-header">
                <h2 className="section-title">Browse by category</h2>
              </div>
              <CategoryBrowser />
            </div>

            {/* Duration section */}
            <div className="section-header" style={{ marginTop: 40 }}>
              <h2 className="section-title">Consolidated editions</h2>
            </div>
            <div className="duration-row">
              {(["1min", "3min", "5min"] as const).map((d) => (
                <button
                  key={d}
                  className="duration-pill"
                  onClick={() => {
                    const audio = document.querySelector(`audio[data-duration="${d}"]`) as HTMLAudioElement | null;
                    if (audio) {
                      audio.scrollIntoView({ behavior: "smooth" });
                      audio.play();
                    }
                  }}
                >
                  <span className="duration-pill-num">{d.replace("min", "")}</span>
                  <span className="duration-pill-label">min</span>
                </button>
              ))}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(["1min", "3min", "5min"] as const).map((d) => (
                <div className="audio-panel" key={d}>
                  <p className="audio-panel-title">
                    📻 {d.replace("min", " min")} consolidated —{" "}
                    {d === "1min" ? "Education, India, World" : d === "3min" ? "All 7 categories" : "Full bulletin"}
                  </p>
                  <audio controls preload="none" data-duration={d}>
                    <source src={`/audio/durations/${d}.mp3`} type="audio/mpeg" />
                  </audio>
                </div>
              ))}
            </div>

            {/* Edition Proof Section */}
            <div id="latest">
              <div className="section-header" style={{ marginTop: 40 }}>
                <h2 className="section-title">Latest demo edition</h2>
              </div>
              {!vm ? (
                <LoadingState />
              ) : vm.page_state === "no_edition" ? (
                <NoEditionState nextScheduled={vm.next_scheduled} />
              ) : (
                <>
                  <EditionCard vm={vm} />
                  {vm.page_state !== "blocked" && <TelegramCTA vm={vm} />}
                </>
              )}
            </div>

            {vm && vm.page_state !== "no_edition" && (
              <>
                <div id="rundown">
                  <div className="section-header">
                    <h2 className="section-title">Today's Rundown</h2>
                  </div>
                  <Rundown vm={vm} />
                </div>

                <EditorialIntake vm={vm} />

                <div id="transcript">
                  <Transcript vm={vm} />
                </div>

                <div id="timeline">
                  <AgentTimeline vm={vm} />
                </div>

                <div id="ledger">
                  <ClaimLedger vm={vm} />
                </div>

                <BlockedExample vm={vm} />
                <PreviousEditions vm={vm} />
                <Disclosure />
              </>
            )}
          </div>
        </div>
      </div>

      <NowPlaying vm={vm} />

      <div className="debug-toolbar" role="toolbar" aria-label="Inspect demo states">
        <span>Inspect:</span>
        {stateOptions.map((name) => (
          <button
            key={name}
            className={fixtureName === name ? "active" : ""}
            aria-pressed={fixtureName === name}
            onClick={() => setFixtureName(name)}
          >
            {name.replace(/_/g, " ")}
          </button>
        ))}
      </div>
    </div>
  );
}

export default App;
